import { useContext, useEffect, useState } from "react"
import moment from "moment"
import { AppContext } from "../../../utils/context"

// Utility function
import { fetchCSVExport, safeDataFetch, safeRequest } from "../../../utils/database"

// UI components
import { CosmosButton, CosmosSelect, CosmosOption, CosmosIconAuthor, CosmosIconClose, CosmosIconDownload, CosmosInput, CosmosSpinner, CosmosText, CosmosTitle, CosmosTooltip } from "@cosmos/web/react"
import VCarbLogo from "../../../assets/images/vcarb-logo-2.png"
import Loading from "../../../components/loading/loading"
import CountryFlag from "../../../components/flag/flag"
import GuestRow from "../../../components/guest/row"
import GuestForm from "../../../components/guest/form"

import { eventRedemptions } from "../../../utils/data/additionalRedemptions"

// Returns the markup for viewing the event guest list
export default function Guests({ country, location, countryCode, eventID, eventName, eventDates }) {
	const [loading, setLoading] = useState(true)
	const [input, setInput] = useState("")
	const [guests, setGuests] = useState([])
	const [guestCount, setGuestCount] = useState(0)
	const [filteredGuests, setFilteredGuests] = useState([])
	const [checkedInGuests, setCheckedInGuests] = useState(0)
	const [processingID, setProcessingID] = useState(null)
	const [viewingPin, setViewingPin] = useState({})
	const [addingGuest, setAddingGuest] = useState(false)
	const [editingGuest, setEditingGuest] = useState(null)
	const [exporting, setExporting] = useState(false)
	const [tours, setTours] = useState([])
	const [availableTours, setAvailableTours] = useState([])
	const [tourGuestID, setTourGuestID] = useState(null)
	const [joinTourID, setJoinTourID] = useState(null)
	const [joiningTour, setJoiningTour] = useState(false)
	const [withoutGiftBag, setWithoutGiftBag] = useState(0)
	const [groups, setGroups] = useState([])
	const [eventGroups, setEventGroups] = useState([])
	const [usePin, setUsePin] = useState(false)
	const [checkIn, setCheckIn] = useState(false)

	// is checkin available - the current date is in event dates
	const today = moment().format("DD/MM/YYYY")
	const checkInAvailable =
		eventDates &&
		Object.keys(eventDates).some((date) => {
			return date === today
		})

	// State used for when deleting guest
	const [deletingGuest, setDeletingGuest] = useState(null)
	const [tourErrors, setTourErrors] = useState(null)

	const [guestListUpdated, setGuestListUpdated] = useState(false)

	const { user } = useContext(AppContext)

	// Different redemption types
	const redemptionTypes = [...(eventRedemptions[eventID] || [])]

	// On component mount
	useEffect(() => {
		fetchEventGuestList()
	}, [])

	// Add an effect to monitor state updates
	useEffect(() => {
		if (guestListUpdated) {
			setGuestListUpdated(false) // Reset the flag
		}
	}, [guests, guestListUpdated])

	// On input change
	useEffect(() => {
		filterGuestResults()
	}, [guests, input])

	// When the guests are updated
	useEffect(() => {
		setGuestCount(guests.length)

		const checkedInGuests = guests.filter((guest) => guest.checked_in != null)

		// Were there any found?
		if (checkedInGuests && checkedInGuests.length > 0) {
			// Set the length of this new array into the state
			setCheckedInGuests(checkedInGuests.length)
		} else {
			// Otherwise just reset the count back to 0
			setCheckedInGuests(0)
		}
		// }
	}, [guests])

	useEffect(() => {
		// Fetch an updated list of the groups
		const fetchAllGroups = async () => {
			// Fetch the groups list
			const groupsList = await safeDataFetch("GET", "/groups", {}, "GROUPS")

			// Setup a new object
			let groupsObj = {}

			// Loop over the groups on the response and write them into the object
			groupsList.forEach((group) => {
				groupsObj[group.id] = group.name
			})

			// Set it into the state
			setGroups(groupsObj)

			// And then fetch the event groups
			fetchEventGroups()
		}

		// Fetch the entire list of available groups
		fetchAllGroups()
	}, [])

	// Fetch the guest list for a given event ID
	const fetchEventGuestList = async () => {
		// Pull a list of the event guests
		const eventGuests = await safeDataFetch("GET", `/guest_list/${eventID}`, {}, `GUEST_LIST_${eventID}`)

		return new Promise((resolve) => {
			// Set them into the state
			if (eventGuests.length > 0) {
				setGuests(eventGuests)
				setFilteredGuests(eventGuests)

				// Get a count for the number of guests where gift_recieved is false
				const noGiftBagYet = eventGuests.filter((guest) => {
					return !guest.gift_received || guest.gift_received === false
				})?.length

				// Set this into the state
				setWithoutGiftBag(noGiftBagYet)
			} else {
				setGuests([])
				setFilteredGuests([])
			}

			// Reset the loading state
			setLoading(false)
			setDeletingGuest(null)
			resolve()
		})
	}

	// Fetch the groups for the given event from the database
	const fetchEventGroups = async () => {
		// Fetch the groups for the given event
		const eventGroupsFetch = await safeDataFetch("GET", `/event/${eventID}/groups`, {}, `EVENT_${eventID}_GROUPS`)

		// Set them into the state
		if (eventGroupsFetch.length > 0) {
			const sortedItems = eventGroupsFetch.slice().sort((a, b) => a.name.localeCompare(b.name))
			setEventGroups(sortedItems)
		}

		// Reset the loading state
		setLoading(false)
	}

	// Filter through the guest results
	const filterGuestResults = () => {
		// If there is no user input
		if (!input || input.length === 0) {
			// Set the filtered list to the original
			setFilteredGuests(guests)

			// Set the global gift bags
			setWithoutGiftBag(
				guests.filter((guest) => {
					return !guest.gift_received || guest.gift_received === false
				})?.length,
			)

			// Stop processing
			return
		}

		// Otherwise filter where either the name or email contain the search input
		const filtered = [...guests].filter((guest) => {
			// Lowercase all the inputs
			const gName = `${guest.first_name?.toLowerCase() || ""} ${guest.last_name?.toLowerCase() || ""}`
			// const gEmail = guest.email?.toLowerCase() || ""
			const gGroup = guest.group_name?.toLowerCase() || ""
			// const checkInDates = guest.check_in_days || ""

			// Lowercase the user input
			const inputLower = input.toLowerCase()

			//  search for the name/group matches
			return gName.includes(inputLower) || gGroup.includes(inputLower)
		})

		// Set the global gift bags
		setWithoutGiftBag(
			filtered.filter((guest) => {
				return !guest.gift_received || guest.gift_received === false
			})?.length,
		)

		// Then set that into the state
		setFilteredGuests(filtered)
	}

	// Toggle the checkIn state of the given guest
	const toggleUserCheckIn = async (guestID, currentStatus, isNow = false) => {
		// Set the guestID into the state for a loading status
		setProcessingID(guestID)
		setTourGuestID(guestID)

		// Reset the tour join state
		setJoinTourID(null)

		// Make the request to the relevant endpoint
		if (currentStatus) {
			// const guest = guests.find((a) => a.id === guestID)
			setUsePin(true)

			// Make the request
			const checkinResponse = await safeRequest("POST", `/guests/checkin/${guestID}`)

			// If the request didn't fail
			if (checkinResponse?.status !== 400 && checkinResponse?.name !== "CheckInFailed" && typeof checkinResponse !== "undefined" && !checkinResponse?.offline) {
				// Get the pin (redemption code) from the response
				const { pin, name, guestgroup, checked_in_time } = checkinResponse

				// Pull a list of the tours
				const eventTours = await safeDataFetch("GET", `/event/${eventID}/tours`, {}, `EVENT_TOURS_${eventID}`)

				// Set them into the state
				if (eventTours.length > 0) {
					setAvailableTours(
						eventTours.filter((tour) => {
							return tour.guest_count < tour.maximum_guests
						}),
					)
					setTours(eventTours)
				} else {
					setTours([])
				}

				// Set this pin into the state to view
				setViewingPin({ pin, name, guestgroup, checked_in_time })
			} else if (checkinResponse?.offline) {
				// If the request was deemed to have been made offline, set the pin locally
				const localGuestsCache = JSON.parse(localStorage.getItem(`vcarb_trackside_GUEST_LIST_${eventID}`)) || {}

				// Find the guest record in the array with the guestID and update the check in time
				const guestRecordIndex = localGuestsCache.findIndex((guest) => guest.id === guestID)

				// Parse some of the data from the guests record
				const { pin, first_name, last_name, group_name } = localGuestsCache[guestRecordIndex]

				// Get the local date & time to the device
				const checkInTimestamp = `${moment().format("DD/MM/YYYY")} ${moment().format("HH:mm:ss")}`

				// Update the check in time
				localGuestsCache[guestRecordIndex].checked_in = checkInTimestamp

				// Update the local storage
				localStorage.setItem(`vcarb_trackside_GUEST_LIST_${eventID}`, JSON.stringify(localGuestsCache))

				// Set this pin into the state to view
				setViewingPin({ pin, name: `${first_name} ${last_name}`, guestgroup: group_name, check_in_string: checkInTimestamp })
			} else {
				console.log("an error occurred")
			}
		} else {
			// Attempt to make the request through the network
			const checkOutResponse = await safeRequest("POST", `/guests/reverse_checkin/${guestID}`, { date: inputDate.option })

			// If the response came back as offline
			if (checkOutResponse.offline) {
				// If the request was deemed to have been made offline, set the pin locally
				const localGuestsCache = JSON.parse(localStorage.getItem(`vcarb_trackside_GUEST_LIST_${eventID}`)) || {}

				// Find the guest record in the array with the guestID and update the check in time
				const guestRecordIndex = localGuestsCache.findIndex((guest) => guest.id === guestID)

				// Parse some of the data from the guests record
				const { check_in_days, check_in_times } = localGuestsCache[guestRecordIndex]

				// The check_in_days should be a comma seperated list of dates the guest is attending
				const daysAttending = check_in_days?.split(",") || []

				// Get a UTC timestamp for the check in time
				const checkInDay = moment(checkInTimestamp, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY")

				// Loop over the days attending with todays date to find the array index
				const dayIndex = daysAttending.findIndex((day) => day === checkInDay)

				// Now split the check_in_times in a similar method
				let checkInTimes = check_in_times?.split(",") || []

				// Let's first check to make sure the check in times and dates array lengths match
				if (daysAttending.length !== checkInTimes.length) {
					// Are the check in times empty?
					if (checkInTimes.length === 0) {
						checkInTimes = Array(daysAttending.length).fill(null)
					}
				}

				// Then write the provided timestamp into the index for the given day
				checkInTimes[dayIndex] = ""

				// Join this array back together as a comma seperated list
				const checkInString = checkInTimes.join(",")

				// Update the check in time
				localGuestsCache[guestRecordIndex].check_in_times = checkInString

				// Update the local storage
				localStorage.setItem(`vcarb_trackside_GUEST_LIST_${eventID}`, JSON.stringify(localGuestsCache))
			}
		}

		// Reset the state and fetch an updated list
		fetchEventGuestList()
		setProcessingID(null)
	}

	// When the input field is updated from the search filter
	const handleInputChange = (value) => {
		// Set it into the state
		setInput(value)
	}

	// Add the guest to the event as an individual
	const addNewGuest = async (formData) => {
		// Make the request
		const addGuestResult = await safeRequest("POST", "/guests", {
			eventID,
			firstName: formData.firstName,
			lastName: formData.lastName,
			group: formData.group,
		})

		// Was there an ID returned from the request?
		if (addGuestResult[0]?.id) {
			const newGuestId = addGuestResult[0]?.id

			// Re-fetch the event guest list
			await fetchEventGuestList()

			// Do we want to check the user in right away (from the add form)
			if (checkIn) {
				await toggleUserCheckIn(newGuestId, true, true)

				// If the voucher is being redeemed
				if (formData.voucher) {
					await safeRequest("POST", `/guests/redeem_voucher`, { guest_id: newGuestId, issued_by: user.name })
				}
			}

			return true
		} else if (addGuestResult.offline) {
			console.log("Request was made offline")
			return true
		} else {
			// @TODO Set a global error here to show under the form
			return false
		}
	}

	// Update an existing guest
	const updateGuest = async (formData) => {
		// Make sure we have an ID
		if (!formData.id) {
			return false
		}

		// Make the request
		const updateGuestResult = await safeRequest("PUT", `/guests/${formData.id}`, {
			event_group_id: formData.group,
		})

		// if redeeming the voucher
		if (formData.voucher) {
			await safeRequest("POST", `/guests/redeem_voucher`, { guest_id: formData.id, issued_by: user.name })
		}

		// Was the update successful?
		if (updateGuestResult) {
			// Re-fetch the event guest list
			await fetchEventGuestList()
			return true
		} else {
			// @TODO Set a global error here to show under the form
			return false
		}
	}

	// Handle saving guest (add or update)
	const handleSaveGuest = async (formData) => {
		if (formData.id) {
			// Update existing guest
			const result = await updateGuest(formData)
			if (result) {
				setEditingGuest(null)
			}
			return result
		} else {
			// Add new guest
			const result = await addNewGuest(formData)
			if (result) {
				resetAddingGuestState()
			}
			return result
		}
	}

	// Reset the state when adding a new guest is cancelled
	const resetAddingGuestState = () => {
		// Reset the inputs and form state
		setCheckIn(false)
		setAddingGuest(false)
	}

	// Handle edit guest request
	const handleEditGuest = (guest) => {
		setEditingGuest(guest)
	}

	// Cancel editing guest
	const cancelEditGuest = () => {
		setEditingGuest(null)
	}

	// Delete a specific guest from the event
	const deleteGuestFromEvent = async (guestID) => {
		setDeletingGuest(guestID)

		// Make the request
		const deleteGuestResponse = await safeRequest("DELETE", `/guests/${guestID}`)

		// Was the request made offline?
		if (deleteGuestResponse?.offline) {
			// Get the local guest list
			const localGuestsCache = JSON.parse(localStorage.getItem(`vcarb_trackside_GUEST_LIST_${eventID}`)) || {}

			// Find the guest record in the array with the guestID
			const guestRecordIndex = localGuestsCache.findIndex((guest) => guest.id === guestID)

			// Use this index to delete the record from the array
			localGuestsCache.splice(guestRecordIndex, 1)

			// Update the local storage
			localStorage.setItem(`vcarb_trackside_GUEST_LIST_${eventID}`, JSON.stringify(localGuestsCache))
		}

		// Then finally refetch the guest list
		fetchEventGuestList()
	}

	// Clear the filtering for the guest list
	const resetFiltering = () => {
		setInput("")
	}

	// Export the guest list to a CSV
	const exportGuestList = async () => {
		// Show the loading spinner
		setExporting(true)

		// Make the request
		await fetchCSVExport("GET", `/guest_list/export/${eventID}/all`, `${eventName.replaceAll(" ", "_")}_Guest_List_ALL`)

		// Reset the loading spinner
		setExporting(false)
	}

	// Process tour join and return to checkin
	const processTourJoin = async () => {
		// Was there a tour chosen?
		if (joinTourID && joinTourID?.length > 0) {
			const tourAdd = await safeRequest("POST", `/tour/guest/${joinTourID}`, { guestID: tourGuestID })
			if (tourAdd?.result) {
				// Then reset the state
				setJoinTourID(null)
				setTourGuestID(null)
				setViewingPin({})
			} else {
				// Otherwise show an error message
				setTourErrors("Cannot add guest to tour, tour is full")
			}
		} else {
			// Then reset the state
			setJoinTourID(null)
			setTourGuestID(null)
			setViewingPin({})
		}
		fetchEventGuestList()
	}

	return (
		<>
			<div className="thosp-header-button">
				<CosmosTitle
					appearance="light"
					size="x-small"
					spacing="none">
					{addingGuest ? "Adding Guest" : editingGuest ? "Editing Guest" : "Guest List"}
					<CosmosText
						appearance="light"
						size="x-small"
						spacing="long-form-bottom">
						{guests?.length} Gift Bags ({withoutGiftBag} Remaining)
					</CosmosText>
				</CosmosTitle>

				{!addingGuest && !editingGuest && (
					<CosmosTooltip
						placement="below"
						content="Add a guest"
						appearance="light">
						<CosmosButton
							shape="square"
							size="small"
							onClick={() => setAddingGuest(!addingGuest)}>
							<CosmosIconAuthor />
						</CosmosButton>
					</CosmosTooltip>
				)}
			</div>

			<Loading
				active={loading}
				label="Fetching guest list..."
			/>

			{guests.length === 0 && !loading && !addingGuest && !editingGuest && (
				<CosmosText
					appearance="light"
					size="small"
					kind="subtle">
					Guest list is currently empty
				</CosmosText>
			)}

			{/* Adding a new guest */}
			{addingGuest && (
				<GuestForm
					eventDates={eventDates}
					eventGroups={eventGroups}
					redemptionTypes={redemptionTypes}
					checkIn={checkIn}
					setCheckIn={setCheckIn}
					onSave={handleSaveGuest}
					onCancel={resetAddingGuestState}
					allGuests={guests}
				/>
			)}

			{/* Editing an existing guest */}
			{editingGuest && (
				<GuestForm
					guest={editingGuest}
					eventDates={eventDates}
					eventGroups={eventGroups}
					redemptionTypes={redemptionTypes}
					checkIn={checkIn}
					setCheckIn={setCheckIn}
					onSave={handleSaveGuest}
					onCancel={cancelEditGuest}
					allGuests={guests}
				/>
			)}

			{guests.length > 0 && !addingGuest && !editingGuest && (
				<>
					{!loading && (
						<div className={["thosp-checkin-count", checkedInGuests === guests.length ? "is-full" : "is-not-full"].join(" ").trim()}>
							<CosmosText
								appearance="light"
								size="x-small"
								spacing="none"
								kind="subtle">
								{checkedInGuests === guests?.length && <>All guests are checked in</>}

								{checkedInGuests !== guests?.length && (
									<>
										<strong>
											{checkedInGuests}/{guestCount}
										</strong>{" "}
										guests checked in
									</>
								)}
							</CosmosText>
						</div>
					)}

					<br />

					<div className="thosp-guest-list-filtering">
						<CosmosInput
							appearance="light"
							label="Search by guest name or group:"
							clearable={true}
							onInputchange={({ detail }) => handleInputChange(detail.value)}
							onInputinput={({ detail }) => handleInputChange(detail.value)}
						/>

						<CosmosButton
							size="small"
							shape="square"
							appearance="light"
							kind="secondary"
							disabled={input.length === 0 || input === ""}
							onClick={() => resetFiltering()}>
							<CosmosIconClose />
						</CosmosButton>

						<CosmosTooltip
							placement="below"
							appearance="light"
							content="Download Guests as CSV">
							<CosmosButton
								size="small"
								shape="square"
								appearance="light"
								kind="secondary"
								onClick={() => exportGuestList()}>
								{exporting ? <CosmosSpinner appearance="light" /> : <CosmosIconDownload />}
							</CosmosButton>
						</CosmosTooltip>
					</div>

					<br />

					{input.length > 0 && (
						<CosmosText
							appearance="light"
							size="x-small"
							kind="subtle"
							spacing="long-form-bottom">
							Filtering for: {input}
						</CosmosText>
					)}

					{filteredGuests.length === 0 && !loading && (
						<CosmosText
							appearance="light"
							size="small">
							No guests found with those filters
						</CosmosText>
					)}

					{filteredGuests.map((guest) => (
						<GuestRow
							id={guest.id}
							key={guest.id}
							name={`${guest.first_name} ${guest.last_name || ""}`}
							group={guest.group_name}
							redeemed={guest.gift_received}
							redeemedAt={guest.gift_received_date}
							pin={guest.pin}
							check_in_available={checkInAvailable}
							loading={processingID === guest.id}
							deleteFromEvent={() => deleteGuestFromEvent(guest.id)}
							deleting={deletingGuest === guest.id ? true : false}
							checked_in={guest.checked_in}
							toggleCheckIn={(guestID, status) => toggleUserCheckIn(guestID, status, false)}
							tour_name={guest.tour_name}
							tour_start={guest.start_time}
							tour_guide={guest.guide}
							editGuest={() => handleEditGuest(guest)}
						/>
					))}
				</>
			)}

			{viewingPin.pin && usePin && (
				<section className="thosp-viewing-checkin-pin">
					<img
						className="vcarb-logo"
						src={VCarbLogo}
						alt="Visa Cash App Racing Bulls"
					/>

					<CosmosTitle appearance="light">Welcome to {country}!</CosmosTitle>
					<div className="thosp-event-location">
						<CountryFlag countryCode={countryCode} />

						<CosmosText
							appearance="light"
							size="x-small"
							spacing="none">
							{location}
						</CosmosText>
					</div>

					<br />

					<CosmosTitle
						appearance="light"
						className="thosp-pin"
						size="xx-large">
						{viewingPin.pin}
					</CosmosTitle>
					<CosmosText
						appearance="light"
						size="small"
						spacing="none"
						weight="bold">
						{viewingPin.name}
					</CosmosText>
					<CosmosText
						appearance="light"
						kind="subtle"
						size="x-small"
						spacing="none">
						{moment(viewingPin.checked_in_time).format("dddd DD MMMM HH:mm")}
					</CosmosText>

					<br />
					<br />

					<CosmosSelect
						className="thosp-tour-checkin-select"
						appearance="light"
						disabled={!tours || tours.length === 0}
						placeholder={tours.length > 0 ? "Join a tour" : "No tours available"}
						clearable={true}
						invalid={tourErrors && tourErrors.length > 0}
						validationMessage={tourErrors}
						onInputchange={({ detail }) => setJoinTourID(detail.value)}>
						{availableTours.map((tour) => {
							const fnameLetter = tour.name.split(" ")[0].charAt(0).toUpperCase()
							const lname = tour.name.split(" ")[1]

							return (
								<CosmosOption
									value={tour.id}
									spacing="">
									{moment(tour.start_time).format("DD/MM HH:mm")} {tour.title} w/ {fnameLetter} {lname}
								</CosmosOption>
							)
						})}
					</CosmosSelect>

					<br />

					<CosmosButton
						size="small"
						onClick={() => processTourJoin({})}
						busy={joiningTour}>
						{joinTourID?.length > 0 ? "Join Tour & Return to check in" : "Return to check in"}
					</CosmosButton>
				</section>
			)}
		</>
	)
}
